import React, { useMemo, useState } from 'react';
import { Plus, MessageSquare, Trash2 } from 'lucide-react';
import { useChat } from '../../hooks/useChat';
import { ConfirmModal } from '../Common/ConfirmModal';
import { ChatSession } from '../../types/chat';

const JOUR_MS = 24 * 3600 * 1000;

// Regroupe les conversations par dernière activité. L'ordre de l'API (plus récente
// d'abord) est conservé à l'intérieur de chaque groupe.
export const groupeParDate = (sessions: ChatSession[], maintenant: Date = new Date()) => {
  const debutJour = new Date(maintenant.getFullYear(), maintenant.getMonth(), maintenant.getDate()).getTime();
  const groupes: { titre: string; sessions: ChatSession[] }[] = [
    { titre: "Aujourd'hui", sessions: [] },
    { titre: '7 derniers jours', sessions: [] },
    { titre: 'Plus ancien', sessions: [] },
  ];
  sessions.forEach((s) => {
    const t = new Date(s.last_activity_at || s.created_at).getTime();
    const rang = Number.isNaN(t) || t >= debutJour ? 0 : t >= debutJour - 7 * JOUR_MS ? 1 : 2;
    groupes[rang].sessions.push(s);
  });
  return groupes.filter((g) => g.sessions.length > 0);
};

interface ChatSidebarProps {
  onCloseMobile?: () => void;
}

export const ChatSidebar: React.FC<ChatSidebarProps> = ({ onCloseMobile }) => {
  const {
    sessions,
    currentSessionId,
    loadSession,
    startNewSession,
    deleteSession,
    hasMoreSessions,
    loadMoreSessions,
  } = useChat();

  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);
  const groupes = useMemo(() => groupeParDate(sessions), [sessions]);
  const titreASupprimer = sessions.find((s) => s.id === sessionToDelete)?.title;

  const handleSessionClick = (id: string) => {
    loadSession(id);
    if (onCloseMobile) onCloseMobile();
  };

  const handleNewChat = () => {
    startNewSession();
    if (onCloseMobile) onCloseMobile();
  };

  const confirmDelete = () => {
    if (sessionToDelete) {
      deleteSession(sessionToDelete);
      setSessionToDelete(null);
    }
  };

  return (
    <>
      <div className="chat-sidebar">
        <div className="chat-sidebar-header">
          <button className="new-chat-btn" onClick={handleNewChat}>
            <Plus size={18} />
            <span>Nouvelle Discussion</span>
          </button>
        </div>

        <div className="chat-sidebar-content">
          <div className="sessions-list">
            {sessions.length === 0 && <div className="no-sessions">Aucune conversation pour l'instant</div>}
            {groupes.map((groupe) => (
              <React.Fragment key={groupe.titre}>
                <div className="sidebar-group-title">{groupe.titre}</div>
                {groupe.sessions.map((session) => (
                  <div
                    key={session.id}
                    className={`session-item ${session.id === currentSessionId ? 'active' : ''}`}
                    onClick={() => handleSessionClick(session.id)}
                  >
                    <MessageSquare size={16} className="session-icon" />
                    <span className="session-title">
                      {session.title || "Discussion sans titre"}
                    </span>
                    <button
                      className="delete-session-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSessionToDelete(session.id);
                      }}
                      title="Supprimer"
                      aria-label="Supprimer la conversation"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </React.Fragment>
            ))}
            {/* Au-delà de 20 conversations : pagination (elles étaient invisibles). */}
            {hasMoreSessions && (
              <button
                type="button"
                className="session-item"
                onClick={() => loadMoreSessions()}
                style={{ justifyContent: 'center', color: 'var(--text-muted)', background: 'none', border: 'none', width: '100%', cursor: 'pointer' }}
              >
                Afficher les conversations plus anciennes
              </button>
            )}
          </div>
        </div>

        <div className="chat-sidebar-footer">
          <div className="nia-brand">
            <div className="nia-avatar-small">N</div>
            <div className="nia-info">
              <div className="nia-name">Nia Analyste</div>
              <div className="nia-status">En ligne</div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal 
        isOpen={!!sessionToDelete}
        title="Supprimer la conversation"
        message={`Supprime définitivement la conversation${titreASupprimer ? ` « ${titreASupprimer} »` : ''} et tous ses messages, sur tous vos appareils. Cette action est irréversible.`}
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        isDestructive={true}
        onConfirm={confirmDelete}
        onCancel={() => setSessionToDelete(null)}
      />
    </>
  );
};
